import { useCallback, type ReactElement } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { Button, IconButton, useViewport } from "@primitives/index";
import { AccountMenu } from "@compositions/index";
import { useAuth } from "../auth/useAuth";

/**
 * Chrome slot contents shared by the admin, settings, and entity pages.
 *
 * `BackToWorkflows` fills the `leading` slot: a labeled button on
 * `expanded`+ and an icon button below. `ChromeActions` fills the `actions`
 * slot with the account menu.
 */

export function BackToWorkflows(): ReactElement {
  const navigate = useNavigate();
  const { isAtLeast } = useViewport();
  const handleBack = useCallback(() => void navigate("/workflows"), [navigate]);

  if (isAtLeast("expanded")) {
    return (
      <Button variant="ghost" size="sm" onClick={handleBack}>
        <ArrowLeft size={14} strokeWidth={2.25} aria-hidden="true" />
        Workflows
      </Button>
    );
  }
  return (
    <IconButton
      icon={ArrowLeft}
      aria-label="Back to workflows"
      tooltip="Back to workflows"
      onClick={handleBack}
    />
  );
}

export function ChromeActions(): ReactElement {
  const auth = useAuth();
  const navigate = useNavigate();
  const handleSignOut = useCallback(() => {
    void auth.signOut();
  }, [auth]);
  const handleOpenSettings = useCallback(() => void navigate("/settings"), [navigate]);

  return (
    <AccountMenu
      userName={auth.account?.name ?? "User"}
      userEmail={auth.account?.username ?? ""}
      onOpenSettings={handleOpenSettings}
      onSignOut={handleSignOut}
    />
  );
}
